import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { useLanguage } from '../hooks/useLanguage';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { InteractiveMap } from '../components/InteractiveMap';
import { clientLocations, officeLocations } from '../data/mapLocations';
interface LocationDot {
  id: string;
  name: string;
  x: number; // percentage from left
  y: number; // percentage from top
}
export function ContactPage() {
  const {
    t,
    setLocale,
    direction
  } = useLanguage();
  const location = useLocation();
  const {
    ref: heroRef,
    isVisible: heroVisible
  } = useScrollReveal();
  const {
    ref: officesRef,
    isVisible: officesVisible
  } = useScrollReveal();
  const {
    ref: mapRef,
    isVisible: mapVisible
  } = useScrollReveal();
  const [activeTab, setActiveTab] = useState<'clients' | 'offices'>('clients');
  useEffect(() => {
    if (location.pathname.startsWith('/ar')) {
      setLocale('ar');
    } else {
      setLocale('en');
    }
    window.scrollTo(0, 0);
  }, [location.pathname, setLocale]);
  const currentLocations = activeTab === 'clients' ? clientLocations : officeLocations;
  const offices = [{
    title: t('contactPage.offices.cyprus.title'),
    lines: ['3 Zachariadhes Court', '15 Nicodemou, Mylona Street', 'Larnaca 6010 - Cyprus', 'T: +357 24 828 711']
  }, {
    title: t('contactPage.offices.lebanon.title'),
    lines: ['150 Commodore Street', 'Hamra, Beirut 1103 2120 - Lebanon', 'T: +961 1 742 555']
  }, {
    title: t('contactPage.offices.iraq.title'),
    lines: ['Karada Dakhel - Dis.901,', 'St.3, Bldg 4, Baghdad – Iraq', 'T: +964 7813 220055']
  }, {
    title: t('contactPage.offices.dataCenter.title'),
    lines: ['Digital Reality Colocation Site', 'Athens, Greece']
  }];
  return <div className="min-h-screen w-full bg-white text-slate-900" dir={direction} data-id="element-667">
      <Header variant="dark" data-id="element-668" />

      <main data-id="element-669">
        {/* White header background spacer */}
        <div className="bg-white h-20" data-id="element-670" />

        {/* 1. Hero Banner - same as CareersPage */}
        <section className="gradient-dark-horizontal py-16 md:py-20 relative overflow-hidden" ref={heroRef} aria-label="Contact Us Hero" data-id="element-671">
          {/* Top-left decorative SVG */}
          <img src="https://cdn.magicpatterns.com/uploads/oSPxnKHLzspYqMxuH2SAJd/inner-top-left.svg" alt="" role="presentation" aria-hidden="true" className="absolute top-0 left-0 w-[200px] md:w-[300px] h-auto pointer-events-none opacity-20" data-id="element-672" />

          <div className="container mx-auto px-6 md:px-12" data-id="element-673">
            <h1 className={`text-white font-bold leading-[1.1] mb-4 uppercase text-[32px] md:text-[44px] lg:text-[50px] reveal-hidden ${heroVisible ? 'animate-fade-up' : ''}`} data-id="element-674">
              {t('contactPage.hero.title')}
            </h1>
            <p className={`text-white/80 text-base md:text-lg whitespace-pre-line leading-relaxed max-w-lg reveal-hidden ${heroVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-675">
              {t('contactPage.hero.subtitle')}
            </p>
          </div>
        </section>

        {/* Wrapper for sections 2 and 3 to allow SVG spanning */}
        <div className="relative" style={{
        overflow: 'visible'
      }} data-id="element-676">
          {/* Top-right decorative circle SVG - placed on wrapper so it's not clipped */}
          <img src="https://cdn.magicpatterns.com/uploads/aVLvkvrFLX79R8UEYVhXQZ/about-circle.svg" alt="" role="presentation" aria-hidden="true" className="absolute pointer-events-none opacity-50" style={{
          top: 0,
          right: 0,
          width: '40%',
          zIndex: 5
        }} data-id="element-677" />

          {/* Left spanning SVG - placed on wrapper so it's not clipped by either section */}
          <img src="https://cdn.magicpatterns.com/uploads/i7gpwPgxGpJxXDHwRZ8TSv/7.svg" alt="" role="presentation" aria-hidden="true" className="absolute pointer-events-none opacity-50" style={{
          left: 0,
          top: '50%',
          transform: 'translateY(-50%)',
          width: '480px',
          zIndex: 5
        }} data-id="element-678" />

          {/* 2. Offices Section - 4 columns, same size as careers second section */}
          <section className="py-20 md:py-28 bg-white relative" ref={officesRef} aria-label="Our Offices" style={{
          overflow: 'visible'
        }} data-id="element-679">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-680">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12" data-id="element-681">
                {offices.map((office, index) => <div key={index} className={`reveal-hidden ${officesVisible ? 'animate-fade-up' : ''}`} style={{
                animationDelay: `${index * 100}ms`
              }} data-id="element-682">
                    <h3 className="text-[#061D76] font-bold text-base md:text-lg mb-4" data-id="element-683">
                      {office.title}
                    </h3>
                    <div className="space-y-1" data-id="element-684">
                      {office.lines.map((line, lineIndex) => <p key={lineIndex} className="text-[#061D76] text-sm md:text-base leading-relaxed" data-id="element-685">
                          {line}
                        </p>)}
                    </div>
                  </div>)}
              </div>
            </div>
          </section>

          {/* 3. Map Section - same background as App Features */}
          <section className="gradient-light relative" ref={mapRef} aria-label="Global Presence Map" style={{
          paddingTop: '4rem',
          paddingBottom: '4rem',
          minHeight: '70vh',
          overflow: 'visible'
        }} data-id="element-686">
            <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-687">
              {/* Tabs */}
              <div className={`flex gap-6 mb-8 reveal-hidden ${mapVisible ? 'animate-fade-up' : ''}`} data-id="element-688">
                <button onClick={() => setActiveTab('clients')} className={`text-base md:text-lg font-semibold transition-colors ${activeTab === 'clients' ? 'text-[#061D76] underline underline-offset-4' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-689">
                  {t('contactPage.map.ourClients')}
                </button>
                <button onClick={() => setActiveTab('offices')} className={`text-base md:text-lg font-semibold transition-colors ${activeTab === 'offices' ? 'text-[#061D76] underline underline-offset-4' : 'text-[#25B8D6] hover:text-[#061D76]'}`} data-id="element-690">
                  {t('contactPage.map.ourOffices')}
                </button>
              </div>

              {/* Map Container */}
              <div className={`relative w-full reveal-hidden ${mapVisible ? 'animate-fade-up delay-200' : ''}`} data-id="element-691">
                <InteractiveMap locations={currentLocations} mapSrc="https://cdn.magicpatterns.com/uploads/24yKzhQTsKSKj2Z6kqe6yX/map.svg" data-id="element-692" />
              </div>
            </div>
          </section>
        </div>
      </main>

      <Footer data-id="element-693" />
    </div>;
}